import { store, Unsubscribe } from "@stencil/redux";
import { IContactInfo, ConversationTypeEnum } from "@auvious/integrations";
import {
  Component,
  ComponentInterface,
  h,
  Prop,
  Event,
  EventEmitter,
  State,
  Watch,
  Element,
} from "@stencil/core";

import {
  ICustomField,
  ISelectOption,
  IThemeOptions,
  RegistrationTypeEnum,
} from "../../models";
import {
  TranslateService,
  ThemeService,
  AppConfigService,
} from "../../services";
import { RootState } from "../../store/states";
import { NotificationService } from "../../services/notification.service";
import { Subscription } from "rxjs";
import { ErrorCode } from "../../models/enums";

interface ICustomerState {
  errors?: {
    firstName?: ValidityState;
    lastName?: ValidityState;
    email?: ValidityState;
    subject?: ValidityState;
  };
  isCustomFieldsValid?: boolean;
}

@Component({
  tag: "app-customer",
  styleUrl: "app-customer.scss",
  shadow: true,
})
export class AppCustomer implements ComponentInterface {
  contact: IContactInfo = {};

  @State() state: ICustomerState = {
    errors: {},
    isCustomFieldsValid: true,
  };
  @State() open: boolean = false;

  @Prop() loading: boolean = false;
  @Prop() registrationType: RegistrationTypeEnum;

  @Element() hostRef: HTMLDivElement;

  @Event({ bubbles: false }) completed: EventEmitter<IContactInfo>;

  @Watch("open")
  openChange(newValue: boolean) {
    if (!newValue) {
      this.contact = {};
      this.setViewState({ errors: {} });

      if (this.registrationType !== RegistrationTypeEnum.custom) {
        // @ts-ignore
        for (let item of this.form.elements) {
          item.value = null;
        }
      }
    } else {
      if (this.registrationType === RegistrationTypeEnum.custom) {
        if (this.customFieldsValid()) {
          this.setupCustomFields();
        } else {
          this.state = { ...this.state, isCustomFieldsValid: false };
        }
      }
    }
  }

  @State() themeOptions: IThemeOptions;

  @Prop({ mutable: true }) $conversationType: ConversationTypeEnum;
  @Prop({ mutable: true }) $customerRequest: IContactInfo;

  private form: HTMLFormElement;
  private storeUnsubscribe: Unsubscribe;
  private configSubscription: Subscription;
  private customFields: ICustomField[] = [];

  constructor() {
    ThemeService.optionsAvailable().subscribe((o) => (this.themeOptions = o));
  }

  connectedCallback() {
    if (this.storeUnsubscribe) this.storeUnsubscribe();

    this.storeUnsubscribe = store.mapStateToProps(
      this,
      (rootState: RootState) => {
        const {
          conversation: { type, customerRequest },
        } = rootState;
        return { $conversationType: type, $customerRequest: customerRequest };
      }
    );
    this.configSubscription = AppConfigService.optionsReady.subscribe(
      (config) => {
        this.customFields = config.customFields;
      }
    );
  }

  componentDidLoad(): void {
    this.open = true;
  }

  disconnectedCallback() {
    this.open = false;
    this.storeUnsubscribe();
    this.configSubscription.unsubscribe();
    this.customFields = [];
  }

  setupCustomFields() {
    // set default values
    AppConfigService.customFields.forEach((field) => {
      this.contact = {
        ...this.contact,
        [field.name]:
          field.type === "select"
            ? this.$customerRequest?.[field.name] ??
              field.options?.find((opt) => !!opt.value).value //?.[0]?.value
            : this.$customerRequest?.[field.name],
      };
    });
  }

  private setViewState(props: ICustomerState) {
    this.state = { ...this.state, ...props };
  }

  private customFieldsValid(): boolean {
    let error = false;

    const propError = (message, errorCode) => {
      NotificationService.error(message, { ttl: -1, errorCode });
      error = true;
    };

    if (AppConfigService.customFields.length === 0) {
      propError(
        '"registration-type" is set to "custom" but custom fields are missing.',
        ErrorCode.REGISTRATION_MISSING_CUSTOM_FIELDS
      );
    } else {
      let valid = true;
      AppConfigService.customFields.forEach((field) => {
        valid =
          valid &&
          !!field.name &&
          field.type &&
          ((field.type === "select" && field.options?.length > 0) ||
            field.type !== "select");
      });
      if (!valid) {
        propError(
          'invalid custom fields. Please make sure "name" and "type" are set and "select" has one or more options.',
          ErrorCode.REGISTRATION_INVALID_CUSTOM_FIELDS
        );
      }
    }
    return !error;
  }

  tryComplete() {
    if (this.isValid()) {
      this.completed.emit(this.contact);
    }
  }

  handleChange(event) {
    try {
      this.contact[event.target.name] = event.target.value;
      this.state = {
        ...this.state,
        errors: {
          ...this.state.errors,
          [event.target.name]: event.target.checkValidity()
            ? null
            : (event.target.validity as ValidityState),
        },
      };
    } catch (ex) {
      NotificationService.error("Field validation error", { body: ex.message });
    }
  }

  isValid(): boolean {
    let valid = true;
    // @ts-ignore
    for (let item of this.form.elements) {
      this.state = {
        ...this.state,
        errors: {
          ...this.state.errors,
          [item.name]: item.checkValidity()
            ? null
            : (item.validity as ValidityState),
        },
      };
      valid = valid && item.checkValidity();
    }
    return valid;
  }

  private renderError(field: ICustomField, warnTextStyle) {
    return (
      <div style={warnTextStyle} class="error-text">
        {(this.state.errors[field.name] as ValidityState).valueMissing && (
          <div>{TranslateService.instant("Required")}</div>
        )}
        {(this.state.errors[field.name] as ValidityState).typeMismatch && (
          <div>{TranslateService.instant("Not an email")}</div>
        )}
        {(this.state.errors[field.name] as ValidityState).badInput && (
          <div>{TranslateService.instant("Not an number")}</div>
        )}
        {(this.state.errors[field.name] as ValidityState).patternMismatch && (
          <div>
            {field.patternErrorMessage || TranslateService.instant("Invalid")}
          </div>
        )}
      </div>
    );
  }

  private renderText(field: ICustomField, warnTextStyle, warnInputStyle) {
    return (
      <div
        class={{
          "form-field": true,
          "form-field-error": !!this.state.errors[field.name],
        }}
      >
        <input
          style={!!this.state.errors[field.name] ? warnInputStyle : null}
          name={field.name}
          type="text"
          required={field.required}
          onInput={this.handleChange.bind(this)}
          placeholder={field.label}
          value={this.$customerRequest?.[field.name]}
          pattern={field.pattern}
        />
        {!!this.state.errors[field.name] &&
          this.renderError(field, warnTextStyle)}
      </div>
    );
  }

  private renderEmail(field: ICustomField, warnTextStyle, warnInputStyle) {
    return (
      <div
        class={{
          "form-field": true,
          "form-field-error": !!this.state.errors[field.name],
        }}
      >
        <input
          style={!!this.state.errors[field.name] ? warnInputStyle : null}
          name={field.name}
          type="email"
          required={field.required}
          onInput={this.handleChange.bind(this)}
          placeholder={field.label}
          value={this.$customerRequest?.[field.name]}
          pattern={field.pattern}
        />
        {!!this.state.errors[field.name] &&
          this.renderError(field, warnTextStyle)}
      </div>
    );
  }

  private renderNumber(field: ICustomField, warnTextStyle, warnInputStyle) {
    return (
      <div
        class={{
          "form-field": true,
          "form-field-error": !!this.state.errors[field.name],
        }}
      >
        <input
          style={!!this.state.errors[field.name] ? warnInputStyle : null}
          name={field.name}
          type="number"
          required={field.required}
          onInput={this.handleChange.bind(this)}
          placeholder={field.label}
          value={this.$customerRequest?.[field.name]}
          pattern={field.pattern}
        />
        {!!this.state.errors[field.name] &&
          this.renderError(field, warnTextStyle)}
      </div>
    );
  }

  private renderNotSupported(field: ICustomField) {
    return (
      <div
        class={{
          "form-field": true,
          "form-field-not-supported": true,
        }}
      >
        <av-translate>Unsupported field type:</av-translate> {field.type}
      </div>
    );
  }

  private renderSelect(field: ICustomField, warnTextStyle, warnInputStyle) {
    let optionSelected = false;
    const isSelected = (option: ISelectOption) => {
      if (optionSelected) return false;
      if (!!option.value) {
        optionSelected = true;
        return true;
      }
    };
    return (
      <div
        class={{
          "form-field": true,
          "form-field-select": true,
          "form-field-error": !!this.state.errors[field.name],
        }}
      >
        {!!field.label && <label>{field.label}</label>}
        <select
          style={!!this.state.errors[field.name] ? warnInputStyle : null}
          name={field.name}
          required={field.required}
          onChange={this.handleChange.bind(this)}
        >
          {field.options?.map((option) => {
            return (
              <option
                value={option.value || ""}
                selected={
                  (!!option.value &&
                    this.$customerRequest?.[field.name] === option.value) ||
                  isSelected(option)
                }
                disabled={!option.value}
              >
                {option.label}
              </option>
            );
          })}
        </select>
        {!!this.state.errors[field.name] &&
          this.renderError(field, warnTextStyle)}
      </div>
    );
  }

  render() {
    const firstnameText = TranslateService.instant("first name");
    const lastnameText = TranslateService.instant("last name");
    const emailText = TranslateService.instant("email");
    const subjectText = TranslateService.instant("subject");
    let buttonText;

    switch (this.$conversationType) {
      case ConversationTypeEnum.chat:
        buttonText = "Start chat";
        break;
      case ConversationTypeEnum.videoCall:
      case ConversationTypeEnum.voiceCall:
        buttonText = "Start call";
        break;
    }

    buttonText = TranslateService.instant(buttonText);

    const warnTextStyle = { color: this.themeOptions.colorWarn };
    const warnInputStyle = { borderBottomColor: this.themeOptions.colorWarn };

    return (
      <av-card
        aria-label={TranslateService.instant("My information")}
        role="dialog"
        open={this.open}
        class="card-customer"
      >
        <av-card-header closeHidden={true}>
          <h3 class="card-customer-header">
            <av-translate>Register</av-translate>
          </h3>
        </av-card-header>
        <div slot="card-body">
          {!!this.$conversationType &&
            this.registrationType === RegistrationTypeEnum.custom &&
            this.state.isCustomFieldsValid && (
              <form ref={(el) => (this.form = el)}>
                {this.customFields.map((field) => {
                  switch (field.type) {
                    case "text":
                      return this.renderText(
                        field,
                        warnTextStyle,
                        warnInputStyle
                      );
                    case "email":
                      return this.renderEmail(
                        field,
                        warnTextStyle,
                        warnInputStyle
                      );
                    case "number":
                      return this.renderNumber(
                        field,
                        warnTextStyle,
                        warnInputStyle
                      );
                    case "select":
                      return this.renderSelect(
                        field,
                        warnTextStyle,
                        warnInputStyle
                      );
                    default:
                      return this.renderNotSupported(field);
                  }
                })}
                <div class="actions">
                  <av-button
                    color="accent"
                    onClick={this.tryComplete.bind(this)}
                  >
                    {buttonText}
                  </av-button>
                </div>
              </form>
            )}

          {this.registrationType === RegistrationTypeEnum.custom &&
            !this.state.isCustomFieldsValid && (
              <p class="text-muted">Invalid fields</p>
            )}

          {this.registrationType === RegistrationTypeEnum.nameEmailSubject && (
            <form ref={(el) => (this.form = el)}>
              <div
                class={{
                  "form-field": true,
                  "form-field-error": !!this.state.errors.firstName,
                }}
              >
                <input
                  style={!!this.state.errors.firstName ? warnInputStyle : null}
                  name="firstName"
                  type="text"
                  required
                  value={this.$customerRequest?.firstName}
                  onInput={this.handleChange.bind(this)}
                  placeholder={firstnameText}
                />
                {!!this.state.errors.firstName &&
                  this.state.errors.firstName.valueMissing && (
                    <span style={warnTextStyle} class="error-text">
                      <av-translate>Required</av-translate>
                    </span>
                  )}
              </div>
              <div
                class={{
                  "form-field": true,
                  "form-field-error": !!this.state.errors.lastName,
                }}
              >
                <input
                  style={!!this.state.errors.lastName ? warnInputStyle : null}
                  name="lastName"
                  type="text"
                  required
                  value={this.$customerRequest?.lastName}
                  onInput={this.handleChange.bind(this)}
                  placeholder={lastnameText}
                />
                {!!this.state.errors.lastName &&
                  this.state.errors.lastName.valueMissing && (
                    <span style={warnTextStyle} class="error-text">
                      <av-translate>Required</av-translate>
                    </span>
                  )}
              </div>
              <div
                class={{
                  "form-field": true,
                  "form-field-error": !!this.state.errors.email,
                }}
              >
                <input
                  style={!!this.state.errors.email ? warnInputStyle : null}
                  name="email"
                  type="email"
                  required
                  value={this.$customerRequest?.email}
                  onInput={this.handleChange.bind(this)}
                  placeholder={emailText}
                />
                {!!this.state.errors.email &&
                  this.state.errors.email.valueMissing && (
                    <span style={warnTextStyle} class="error-text">
                      <av-translate>Required</av-translate>
                    </span>
                  )}
                {!!this.state.errors.email &&
                  this.state.errors.email.typeMismatch && (
                    <span style={warnTextStyle} class="error-text">
                      <av-translate>Not an email</av-translate>
                    </span>
                  )}
              </div>
              <div
                class={{
                  "form-field": true,
                  "form-field-error": !!this.state.errors.subject,
                }}
              >
                <input
                  style={!!this.state.errors.subject ? warnInputStyle : null}
                  name="subject"
                  type="text"
                  required
                  value={this.$customerRequest?.subject}
                  onInput={this.handleChange.bind(this)}
                  placeholder={subjectText}
                />
                {!!this.state.errors.subject &&
                  this.state.errors.subject.valueMissing && (
                    <span style={warnTextStyle} class="error-text">
                      <av-translate>Required</av-translate>
                    </span>
                  )}
              </div>
              {!!AppConfigService.captchaSiteKey && (
                <av-captcha-disclaimer></av-captcha-disclaimer>
              )}
              <div class="actions">
                <av-button
                  color="accent"
                  loading={this.loading}
                  onClick={this.tryComplete.bind(this)}
                >
                  {buttonText}
                </av-button>
              </div>
            </form>
          )}
        </div>
      </av-card>
    );
  }
}
